import { useContext } from "react";
import useApi from "./useApi";
import { UserAccountContext } from "../contexts/UserAccountContext";
import useUserAccount from "./useUserAccount";

const useUserAccountSettings = () => {
    const { getJson, patchJson } = useApi();
    const { userIsCompany } = useUserAccount();
    const { settings, updateSettings, loadingSettings, setLoadingSettings } = useContext(UserAccountContext);

    /**
     * Checks if any of the address fields are missing or empty, recommending an update if so.
     * @returns {boolean}
     */
    const addressUpdateIsRecommended = () => {
        if (!settings || Object.keys(settings).length === 0 || userIsCompany()) {
            return false; // No settings loaded, so we can't recommend an update
        }
        const addressFields = ['streetAddress', 'postalCode', 'city', 'country'];
        return addressFields.some(field => !settings[field] || settings[field].trim() === '');
    }

    /**
     * Fetches user account company settings
     * @returns {Promise<object>}
     */
    const getUserAccountCompanySettings = async () => {
        if (loadingSettings) {
            return; // Prevent multiple simultaneous loads
        }
        // If settings are already loaded, return them
        if (Object.keys(settings).length > 0) {
            return settings;
        }
        setLoadingSettings(true);
        try {
            const response = await getJson('/online/useraccount/companysettings');
            updateSettings(response);
        } finally {
            setLoadingSettings(false);
        }
    }

    /**
     * Updates company settings with trimmed values.
     * @param {object} newSettings - The settings to update
     * @returns {Promise<void>}
     */
    const updateUserAccountCompanySettings = async (newSettings) => {
        const updatedSettings = resolveUpdatedSettings(newSettings);
        if (Object.keys(updatedSettings).length === 0) {
            return; // No changes to update
        }
        await patchJson('/online/useraccount/companysettings', updatedSettings);
        updatedSettings.updatedAt = new Date().toISOString();
        updateSettings(updatedSettings);
    }

    /**
     * Fetches user account settings
     * @returns {Promise<object>}
     */
    const getUserAccountSettings = async () => {
        if (loadingSettings) {
            return; // Prevent multiple simultaneous loads
        }
        // If settings are already loaded, return them
        if (Object.keys(settings).length > 0) {
            return settings;
        }
        setLoadingSettings(true);
        try {
            const response = await getJson('/online/useraccount/settings');
            updateSettings(response);
        } finally {
            setLoadingSettings(false);
        }
    }

    /**
     * Updates user account settings with trimmed values.
     * @param {object} newSettings - The settings to update
     * @returns {Promise<void>}
     */
    const updateUserAccountSettings = async (newSettings) => {
        const updatedSettings = resolveUpdatedSettings(newSettings);
        if (Object.keys(updatedSettings).length === 0) {
            return; // No changes to update
        }
        await patchJson('/online/useraccount/settings', updatedSettings);
        updatedSettings.updatedAt = new Date().toISOString();
        updateSettings(updatedSettings);
    }

    /**
     * Resolves settings that have changed from the current settings.
     * @param newSettings
     * @returns {object} - An object containing only the updated and trimmed settings
     */
    const resolveUpdatedSettings = (newSettings) => {
        const updatedSettings = {};
        // Only update settings that have changed
        for (const key in newSettings) {
            if (settings[key] !== newSettings[key] && !(settings[key] === undefined && newSettings[key] === '')) {
                updatedSettings[key] = newSettings[key];
            }
        }
        return updatedSettings;
    }

    return {
        getUserAccountCompanySettings,
        updateUserAccountCompanySettings,
        getUserAccountSettings,
        updateUserAccountSettings,
        addressUpdateIsRecommended,
        settings
    }
}

export default useUserAccountSettings;
